import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { EMPTY_PATIENT_PAGE_MODEL, PatientModel, PatientPageModel } from '../../global-data-models/patient.model';
import { DEFAULT_PATIENT_PAGE_OPTIONS, OrderDir, PatientService } from '../../global-services/patient.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { PatientPageOptions } from '../../global-services/patient.service';

export interface PatientsMigrationUiState {
  isLoading: boolean;
  showMigrationOptions: boolean;
}

export const EMPTY_PATIENTS_MIGRATE_UI_STATE: PatientsMigrationUiState = { isLoading: false, showMigrationOptions: true };

@Injectable({ providedIn: 'root' })
export class PatientsMigrateFacade {
  private uiState: PatientsMigrationUiState = EMPTY_PATIENTS_MIGRATE_UI_STATE;
  private readonly _uiState$ = new BehaviorSubject<PatientsMigrationUiState>(this.uiState);
  public readonly uiState$ = this._uiState$.asObservable();
  private orgId: string = '';

  public constructor(
    private readonly messageService: GlobalMessageService,
    private readonly loadingService: GlobalLoadingService,
    private readonly patientService: PatientService,
  ) {}

  public async begin(orgId: string): Promise<void> {
    this.loadingService.show();
    try {
      this.orgId = orgId;
      this._uiState$.next(EMPTY_PATIENTS_MIGRATE_UI_STATE);
    } catch (error: Error | any) {
      this.messageService.showError('Migration Begin Error', error.message);
    } finally {
      this.loadingService.hide();
    }
  }

  public async recordMigrationSkipped(): Promise<void> {
    try {
      this.uiState = { showMigrationOptions: false, isLoading: true };
      this._uiState$.next(this.uiState);
      await this.patientService.skipMigration(this.orgId);
      this.messageService.showSuccess('Migration Skipped', 'Patient data migration has been marked as skipped.');
      this._uiState$.next(EMPTY_PATIENTS_MIGRATE_UI_STATE);
    } catch (error: Error | any) {
      this._uiState$.next(EMPTY_PATIENTS_MIGRATE_UI_STATE);
      this.messageService.showError('Migration Skip Error', error.message);
    } finally {
    }
  }

  public async migratePatients(): Promise<void> {
    try {
      this.uiState = { showMigrationOptions: false, isLoading: true };
      this._uiState$.next(this.uiState);
      await this.patientService.migratePatients(this.orgId);
      this.messageService.showSuccess('Migration Completed', 'Patient data migration has been completed successfully.');
      this._uiState$.next(EMPTY_PATIENTS_MIGRATE_UI_STATE);
    } catch (error: Error | any) {
      this._uiState$.next(EMPTY_PATIENTS_MIGRATE_UI_STATE);
      this.messageService.showError('Migration Error', error.message);
    } finally {
    }
  }

  public async recordMigrationAttempted(): Promise<void> {
    try {
      await this.patientService.recordMigrationAttempted(this.orgId);
    } catch (error: Error | any) {
      this.messageService.showError('Migration Attempt Record Error', error.message);
    }
  }
}
